import { motion } from "motion/react";
import { useState } from "react";

export default function HelpView() {
  const [searchQuery, setSearchQuery] = useState("");

  const faqCategories = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "How do I approve a batch?",
          a: "Navigate to the batch details, review all test results, then tap the 'Approve' button at the bottom of the screen.",
        },
        {
          q: "How to generate a quality report?",
          a: "Go to Reports tab, select the batch, and tap 'Generate Report'. The PDF will be created automatically.",
        },
      ],
    },
    {
      category: "Quality Tests",
      questions: [
        {
          q: "What is the acceptable slump range?",
          a: "The acceptable slump range depends on the concrete grade, typically 75-100mm for M30 grade concrete.",
        },
        {
          q: "How often should cube tests be performed?",
          a: "Cube tests should be performed at 7, 14, and 28 days after casting for comprehensive strength analysis.",
        },
      ],
    },
    {
      category: "Delivery Tracking",
      questions: [
        {
          q: "How to track concrete delivery?",
          a: "Go to Delivery tab to see real-time tracking of all active deliveries with vehicle location and ETA.",
        },
        {
          q: "What temperature is considered critical?",
          a: "Concrete temperature above 32°C requires special attention and faster placement to prevent quality issues.",
        },
      ],
    },
  ];

  const contactOptions = [
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M3 4H17C17.5523 4 18 4.44772 18 5V15C18 15.5523 17.5523 16 17 16H3C2.44772 16 2 15.5523 2 15V5C2 4.44772 2.44772 4 3 4Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M2 5L10 11L18 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: "Email Support",
      value: "support@rmcqualityapp.com",
      action: "Send Email",
      color: "#1B2A5B",
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M17 13.5V15.5C17 16.0523 16.5523 16.5 16 16.5H15C8.37258 16.5 3 11.1274 3 4.5V3.5C3 2.94772 3.44772 2.5 4 2.5H6.5C6.77614 2.5 7 2.72386 7 3V5.5C7 5.77614 6.77614 6 6.5 6H5C5 10.1421 8.35786 13.5 12.5 13.5V12C12.5 11.7239 12.7239 11.5 13 11.5H15.5C15.7761 11.5 16 11.7239 16 12V13C16 13.2761 16.2239 13.5 16.5 13.5H17Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: "Phone Support",
      value: "+91 1800-123-4567",
      action: "Call Now",
      color: "#0F3D2E",
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M17 9C17 13.4183 13.4183 17 9 17C7.57619 17 6.24398 16.6052 5.11422 15.9211L3 17L4.07891 14.8858C3.39481 13.756 3 12.4238 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <circle cx="8" cy="11" r="1" fill="currentColor"/>
          <circle cx="11" cy="11" r="1" fill="currentColor"/>
          <circle cx="14" cy="11" r="1" fill="currentColor"/>
        </svg>
      ),
      title: "Live Chat",
      value: "Available 24/7",
      action: "Start Chat",
      color: "#5361A5",
    },
  ];

  return (
    <div className="pb-24">
      <div className="px-5 pt-6 pb-4">
        <h1
          className="text-[#0A0F1C] mb-2"
          style={{
            fontSize: "32px",
            letterSpacing: "-0.025em",
            fontWeight: "700",
          }}
        >
          Help & Support
        </h1>
        <p
          className="text-[#717182]"
          style={{ fontSize: "14px", fontWeight: "500" }}
        >
          We're here to help you
        </p>
      </div>

      {/* Search */}
      <div className="px-5 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <input
            type="text"
            placeholder="Search for help..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white rounded-2xl px-12 py-4 text-[#0A0F1C] placeholder-[#717182] outline-none"
            style={{
              fontSize: "15px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
            }}
          />
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#717182]"
          >
            <circle
              cx="8"
              cy="8"
              r="5"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M12 12l5 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="px-5 mb-6">
        <div
          className="text-[#717182] mb-4"
          style={{
            fontSize: "11px",
            letterSpacing: "0.05em",
            fontWeight: "600",
          }}
        >
          QUICK ACTIONS
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { 
              icon: (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M8 4H24C25.1046 4 26 4.89543 26 6V26C26 27.1046 25.1046 28 24 28H8C6.89543 28 6 27.1046 6 26V6C6 4.89543 6.89543 4 8 4Z" stroke="#1B2A5B" strokeWidth="2"/>
                  <path d="M10 10H22M10 16H22M10 22H18" stroke="#1B2A5B" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              ), 
              label: "User Guide" 
            },
            { 
              icon: (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <rect x="4" y="6" width="24" height="20" rx="2" stroke="#1B2A5B" strokeWidth="2"/>
                  <path d="M12 16L16 12L20 16M16 12V22" stroke="#1B2A5B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ),
              label: "Video Tutorials" 
            },
            { 
              icon: (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M8 4H18L24 10V26C24 27.1046 23.1046 28 22 28H8C6.89543 28 6 27.1046 6 26V6C6 4.89543 6.89543 4 8 4Z" stroke="#1B2A5B" strokeWidth="2"/>
                  <path d="M18 4V10H24M10 16H20M10 20H20" stroke="#1B2A5B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ),
              label: "Documentation" 
            },
            { 
              icon: (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M16 4V16M16 16L10 10M16 16L22 10" stroke="#1B2A5B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="16" cy="16" r="12" stroke="#1B2A5B" strokeWidth="2"/>
                  <path d="M8 20C8 20 10 24 16 24C22 24 24 20 24 20" stroke="#1B2A5B" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              ),
              label: "What's New" 
            },
          ].map((item, index) => (
            <motion.button
              key={item.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 + index * 0.05, duration: 0.4 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white rounded-2xl p-5 text-center"
              style={{
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.03)",
              }}
            >
              <div className="mb-2 flex items-center justify-center">
                {item.icon}
              </div>
              <div
                className="text-[#0A0F1C]"
                style={{ fontSize: "13px", fontWeight: "600" }}
              >
                {item.label}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* FAQs */}
      <div className="px-5 mb-6">
        <div
          className="text-[#717182] mb-4"
          style={{
            fontSize: "11px",
            letterSpacing: "0.05em",
            fontWeight: "600",
          }}
        >
          FREQUENTLY ASKED QUESTIONS
        </div>

        <div className="space-y-4">
          {faqCategories.map((category, catIndex) => (
            <motion.div
              key={category.category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + catIndex * 0.1, duration: 0.5 }}
              className="bg-white rounded-3xl p-5"
              style={{
                boxShadow:
                  "0 2px 4px rgba(0, 0, 0, 0.02), 0 8px 24px rgba(0, 0, 0, 0.06)",
              }}
            >
              <div
                className="text-[#1B2A5B] mb-4"
                style={{
                  fontSize: "13px",
                  letterSpacing: "0.03em",
                  fontWeight: "700",
                }}
              >
                {category.category.toUpperCase()}
              </div>

              <div className="space-y-4">
                {category.questions.map((item, qIndex) => (
                  <div key={qIndex}>
                    <div
                      className="text-[#0A0F1C] mb-2"
                      style={{ fontSize: "15px", fontWeight: "600" }}
                    >
                      {item.q}
                    </div>
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "13px", lineHeight: "1.5" }}
                    >
                      {item.a}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Contact Support */}
      <div className="px-5 mb-6">
        <div
          className="text-[#717182] mb-4"
          style={{
            fontSize: "11px",
            letterSpacing: "0.05em",
            fontWeight: "600",
          }}
        >
          CONTACT SUPPORT
        </div>

        <div className="space-y-3">
          {contactOptions.map((option, index) => (
            <motion.button
              key={option.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + index * 0.08, duration: 0.5 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-white p-5 rounded-2xl text-left flex items-center gap-4"
              style={{
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
              }}
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "#1B2A5B08" }}
              >
                {option.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="text-[#0A0F1C] mb-1"
                  style={{ fontSize: "15px", fontWeight: "600" }}
                >
                  {option.title}
                </div>
                <div
                  className="text-[#717182] truncate"
                  style={{ fontSize: "13px" }}
                >
                  {option.value}
                </div>
              </div>
              <div
                className="px-4 py-2 rounded-xl text-white flex-shrink-0 whitespace-nowrap"
                style={{
                  backgroundColor: option.color,
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                {option.action}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* System Info */}
      <div className="px-5 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="bg-white rounded-2xl p-5"
          style={{
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.03)",
          }}
        >
          <div
            className="text-[#717182] mb-3"
            style={{
              fontSize: "11px",
              letterSpacing: "0.05em",
              fontWeight: "600",
            }}
          >
            SYSTEM INFORMATION
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-[#717182]" style={{ fontSize: "13px" }}>
                App Version
              </span>
              <span
                className="text-[#0A0F1C]"
                style={{ fontSize: "13px", fontWeight: "600" }}
              >
                2.4.1
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#717182]" style={{ fontSize: "13px" }}>
                Last Updated
              </span>
              <span
                className="text-[#0A0F1C]"
                style={{ fontSize: "13px", fontWeight: "600" }}
              >
                Dec 20, 2024
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#717182]" style={{ fontSize: "13px" }}>
                Build Number
              </span>
              <span
                className="text-[#0A0F1C]"
                style={{ fontSize: "13px", fontWeight: "600" }}
              >
                2024.12.2847
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}